"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useCharacter } from './CharacterProvider';
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from '../../../contexts/ThemeProvider';

// Компонент для анимации изменяющихся чисел
const AnimatedValue = ({ value }: { value: number }) => {
  const { isDark } = useTheme();
  const [isIncreasing, setIsIncreasing] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);
  const prevValueRef = useRef(value);

  useEffect(() => {
    // Определяем, увеличивается или уменьшается значение
    if (value > prevValueRef.current) {
      setIsIncreasing(true);
    } else if (value < prevValueRef.current) {
      setIsIncreasing(false);
    }
    prevValueRef.current = value;
    
    // Инкрементируем ключ анимации для принудительного повторного рендеринга и анимации
    setAnimationKey((prev) => prev + 1);
  }, [value]);

  // Определяем базовый цвет текста в зависимости от темы
  const baseColor = isDark ? "#ffffff" : "#000000";

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={animationKey}
        initial={{ opacity: 0.7, scale: 0.9 }}
        animate={
          isIncreasing
            ? {
                opacity: 1,
                scale: [0.9, 1.1, 1],
                color: [baseColor, "#22c55e", baseColor],
              }
            : {
                opacity: 1,
                scale: 1,
                x: [0, -3, 3, -3, 3, 0],
                color: [baseColor, "#ef4444", baseColor],
              }
        }
        exit={{ opacity: 0.7, scale: 0.9, transition: { duration: 0.2 } }}
        transition={{
          duration: 0.4,
          ease: "easeInOut",
        }}
        className="text-2xl font-bold tabular-nums font-heading"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
};

// Компонент для отображения статистики персонажа (левый блок)
export const CharacterStats: React.FC = () => {
  const { character } = useCharacter();

  // Добавляем логирование для отладки
  useEffect(() => {
    console.log('CharacterStats: character updated', character);
  }, [character]);

  return (
    <div className="w-full md:w-1/3 rounded-lg border border-border bg-gradient-to-b from-card to-card/90 text-card-foreground shadow-lg">
      <div className="p-6">
        <h3 className="text-2xl font-semibold mb-12">Hero Stats</h3>
        
        <div className="space-y-6">
          <div className="flex items-center py-2 px-3 hover:bg-secondary/30 rounded-md transition-colors">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-base font-medium">Punch damage</span>
              <div className="min-w-[60px] text-right">
                <AnimatedValue value={character.punchDamage} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center py-2 px-3 hover:bg-secondary/30 rounded-md transition-colors">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-base font-medium">High kick damage</span>
              <div className="min-w-[60px] text-right">
                <AnimatedValue value={character.legDamage} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center py-2 px-3 hover:bg-secondary/30 rounded-md transition-colors">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-base font-medium">Kick damage</span>
              <div className="min-w-[60px] text-right">
                <AnimatedValue value={character.LowKickDamage} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center py-2 px-3 hover:bg-secondary/30 rounded-md transition-colors">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-base font-medium">Block power</span>
              <div className="min-w-[60px] text-right">
                <AnimatedValue value={character.blockPower} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center py-2 px-3 hover:bg-secondary/30 rounded-md transition-colors">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-base font-medium">Evasion chance</span>
              <div className="min-w-[60px] text-right">
                <AnimatedValue value={character.evasion} />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 pt-5 border-t border-border">
          <div className="flex items-center py-2 px-3 bg-primary/10 rounded-md">
            <div className="flex-1 flex justify-between items-center">
              <span className="text-xl font-semibold">Health points</span>
              <div className="min-w-[80px] text-right">
                <span className="text-2xl font-bold tabular-nums font-heading">{character.health}</span>
              </div>
            </div>
          </div>
        </div>
          
        <div className="mt-8 pt-5 border-t border-border">
          <h4 className="text-xl font-semibold mb-3">Special effects:</h4>
          {character.damageBonus > 0 && (
            <div className="flex items-center py-2 px-3 bg-primary/10 rounded-md">
              <span className="text-base font-medium">Снижение урона: </span>
              <div className="ml-2">
                <AnimatedValue value={character.damageBonus} />
                <span className="text-xl font-bold ml-1 font-heading">%</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterStats; 